import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomerSharedService } from '../../../../customer-shared.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { DeviceService } from '../../device.service';
import { CommonService } from '../../../../../../shared/services/common.service';

import * as XLSX from 'xlsx';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-gatewayManagement',
  templateUrl: './gateway.component.html',
  styleUrls: ['./gateway.component.css']
})
export class GatewayManagementComponent implements OnInit {
  @ViewChild('addGatewayFile', { static: false }) addGatewayFile!: ElementRef;

  gatewayList$: any[] = [];
  filteredGatewayList$: any[] = [];

  quickFilter: 'all' | 'active' | 'inActive' = 'all';
  counts = { total_count: 0, active_count: 0, inactive_count: 0 };

  fileList: File[] = [];
  excelErr: string = '';
  excelErrLink: string = '';
  EXPECTED_HEADERS = ['gateway_name', 'location_id', 'serial_number', 'alarm', 'severity', 'last_update_user', 'is_active', 'status']
  isSuccessMessage: boolean = false;

  apiCallLoaders = { isGatewaysLoading: false };
  isConfirmLoading = false;

  locationOptions: any[] = [];
  serialNoOptions: any[] = [];
  severityOptions: any[] = [];

  isExcelModalVisible = false;
  isGatewayModalVisible = false;

  searchText: any;
  searchEnabled = false;
  form!: FormGroup
  customer_id: string;

  isEditMode: boolean = false;
  editData: any = null;

  user_id: string = localStorage.getItem('user_id') || '';

  constructor(
    public customerSharedService: CustomerSharedService,
    public route: ActivatedRoute,
    private fb: FormBuilder,
    public deviceService: DeviceService,
    public commonService: CommonService,
    private ngZone: NgZone,
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit() {
    this.filteredGatewayList$ = [];
    this.initializeForm();
    this.getGatewaysbyCustomerId();
    this.getLocationByCustomerId();
    this.getSerialNo();

    this.severityOptions = [
      { key: '1', value: 'Low' },
      { key: '2', value: 'Medium' },
      { key: '3', value: 'High' }
    ];
  }

  initializeForm() {
    this.form = this.fb.group({
      name: [null, Validators.required],
      location_id: [null, Validators.required],
      serialNo: [null, Validators.required],
      enableAlarm: [false],
      alarm: [{ value: null, disabled: true }]
    });

    // Subscribe to changes in enableAlarm to enable/disable the alarm control
    this.form.get('enableAlarm')?.valueChanges.subscribe(checked => {
      if (checked) {
        this.form.get('alarm')?.enable();
        this.form.get('alarm')?.setValue('High'); // Default to High severity
        this.form.get('alarm')?.setValidators(Validators.required);
      } else {
        this.form.get('alarm')?.disable();
        this.form.get('alarm')?.setValue(null);
        this.form.get('alarm')?.clearValidators();
      }
      this.form.get('alarm')?.updateValueAndValidity();
    });
  }

  refreshHandler(): void {
    this.quickFilter = 'all';
    this.searchText = '';
    this.searchEnabled = false;
    this.gatewayList$ = [];
    this.filteredGatewayList$ = [];
    this.getGatewaysbyCustomerId();
  }

  onFileChange(event: any): void {
    const file = event.target.files;
    const newFiles = Array.from(file);
    for (const file of newFiles as File[]) {
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const isValidType = validExtensions.some((ext) => fileName.endsWith(ext));
      if (!isValidType) {
        this.commonService.message.error(`File ${file.name} is not an allowed file type. Please upload excel file only.`);
        continue;
      }
      this.fileList.push(file);
    }
  }

  removeFile(index: number): void {
    this.fileList.splice(index, 1);
    this.addGatewayFile.nativeElement.value = '';
  }

  validateExcelFile(): boolean {
    if (this.fileList.length === 0) {
      this.commonService.message.error('Please select a file to upload');
      return false;
    }
    return true;
  }

  prepareExcelFormData(): FormData {
    const formData = new FormData();
    formData.append('xcelFile', this.fileList[0], this.fileList[0].name);
    formData.append('userId', this.user_id);
    return formData;
  }

  downloadTemplate() {
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([this.EXPECTED_HEADERS]);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Gateway list');
    XLSX.writeFile(wb, 'Add_gateway_template.xlsx');
  }

  onExcelSubmit() {
    // Validate file selection
    if (!this.validateExcelFile()) {
      return;
    }
    this.resetExcelState();
    this.isConfirmLoading = true;
    // Prepare and submit the form data
    const formData = this.prepareExcelFormData();
    this.deviceService.addGatewayExcel(formData).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        this.handleExcelUploadResponse(response);
        this.isConfirmLoading = false;
      },
      error: (error: any) => {
        this.handleExcelUploadError(error);
        this.isConfirmLoading = false;
      }
    });
  }

  resetExcelState(): void {
    this.excelErr = '';
    this.excelErrLink = '';
    this.isSuccessMessage = false;
  }

  handleExcelUploadResponse(response: any): void {
    if (response?.status === 200) {
      // Check if there's a log file URL in the response
      if (response?.result) {
        this.excelErrLink = response.result;
        console.log('this.excelErrLink', this.excelErrLink);
      }
      // Set success message
      this.isSuccessMessage = true;
      this.excelErr = 'Excel file processed successfully';

      // Only close the modal if there are no errors or warnings
      if (!this.excelErrLink) {
        this.isExcelModalVisible = false;
        this.fileList = []; // Clear the file list
      }
      this.getGatewaysbyCustomerId() // Refresh the asset list
    } else {
      // Handle error responses
      this.isSuccessMessage = false;
      this.excelErr = response?.message || response?.error_message || 'Failed to process excel file';
      if (response?.log_file_url) {
        this.excelErrLink = response.log_file_url;
      }
      this.commonService.message.error(this.excelErr);
    }
  }

  handleExcelUploadError(error: any): void {
    this.isSuccessMessage = false;
    this.excelErr = error?.error?.message || 'An error occurred while uploading the excel file';
    if (error?.error?.log_file_url) {
      this.excelErrLink = error.error.log_file_url;
    }
    this.commonService.message.error(this.excelErr);
    console.error('Error uploading excel:', error);
  }

  downloadLogFile() {
    if (this.excelErrLink) {
      window.open(this.excelErrLink, '_blank');
    }
  }

  getLocationByCustomerId() {
    this.commonService.getLocationList(Number(this.customer_id)).subscribe({
      next: (response: any) => {
        const locations = Array.isArray(response?.data) ? response.data : [];
        this.locationOptions = locations;
      },
      error: (err) => {
        console.error('Error fetching locations', err);
        this.commonService.message.error('Failed to fetch locations');
      }
    })
  };

  getSerialNo() {
    this.deviceService.getSerialNo().pipe(untilDestroyed(this)).subscribe({
      next: (response) => {
        this.serialNoOptions = response?.data;
      },
      error: (err) => {
        console.error('Error fetching serial numbers', err);
      }
    })
  }

  getGatewaysbyCustomerId() {
    this.counts = { total_count: 0, active_count: 0, inactive_count: 0 };

    this.apiCallLoaders.isGatewaysLoading = true;
    this.ngZone.runOutsideAngular(() => {
      this.deviceService.getGatewaysbyCustomerId(this.customer_id).pipe(untilDestroyed(this)).subscribe(response => {
        if (response?.result && response?.status === 200) {
          this.gatewayList$ = response?.result;
          this.filteredGatewayList$ = [...this.gatewayList$]

          this.gatewayList$.forEach((gateway) => {
            this.counts.total_count++;
            if (gateway?.is_active === true) this.counts.active_count++;
            if (gateway?.is_active === false) this.counts.inactive_count++;
          });
          this.filteredGatewayList$ = [...this.gatewayList$];
          this.getSearchDetails('');
          this.ngZone.run(() => {
            this.apiCallLoaders.isGatewaysLoading = false;
          });
        } else {
          this.ngZone.run(() => {
            this.apiCallLoaders.isGatewaysLoading = false;
          });
        }
      });
    });
  }



  getSearchDetails(val: any): void {
    let searchText = val.trim().toLowerCase();
    this.filteredGatewayList$ = this.gatewayList$.filter(gateway => {
      return (gateway.gateway_name && gateway.gateway_name.toLowerCase().includes(searchText)) ||
        (gateway.serial_no.toString() && gateway.serial_no.toString().includes(searchText)) ||
        (gateway.display_name && gateway.display_name.toLowerCase().includes(searchText)) ||
        (gateway.location_id.toString() && gateway.location_id.toString().includes(searchText))
    });
  }

  onSubmit() {
    if (this.form.valid) {
      this.isConfirmLoading = true;

      const formData: any = {
        gateway_name: this.form.get('name')?.value,
        location: this.form.get('location_id')?.value,
        serial_number: this.form.get('serialNo')?.value,
        alarm: this.form.get('enableAlarm')?.value ? true : false,
        severity: this.form.get('enableAlarm')?.value ? this.form.get('alarm')?.value : null,
        last_update_user: localStorage.getItem('admin_name') || '',
        user_id: localStorage.getItem('user_id') || ''
      };

      if (this.isEditMode && this.editData) {
        const gateway_id = this.editData.gateway_id;

        if (gateway_id) {
          formData['gateway_id'] = gateway_id;
        }
      }

      const apiCall = this.isEditMode ?
        this.deviceService.updateGateway(formData) :
        this.deviceService.addGateway(formData);

      apiCall.pipe(untilDestroyed(this)).subscribe({
        next: (response: any) => {
          if (response?.status === 200) {
            this.commonService.message.success(
              this.isEditMode ? 'Gateway updated successfully' : 'Gateway added successfully'
            );
            this.isGatewayModalVisible = false;
            this.getGatewaysbyCustomerId();
            this.form.reset();
          } else {
            this.commonService.message.error(
              response?.message || (this.isEditMode ? 'Failed to update gateway' : 'Failed to add gateway')
            );
          }
          this.isConfirmLoading = false;
        },
        error: (error: any) => {
          this.commonService.message.error(
            this.isEditMode ? 'An error occurred while updating the gateway' : 'An error occurred while adding the gateway'
          );
          console.error('Error:', error);
          this.isConfirmLoading = false;
        }
      });
    } else {
      // Mark all fields as touched to trigger validation messages
      Object.values(this.form.controls).forEach(control => {
        control.markAsTouched();
      });
    }
  }

  filterData(): void {
    if (this.quickFilter === 'all') {
      this.filteredGatewayList$ = this.gatewayList$;
    } else if (this.quickFilter === 'active') {
      this.filteredGatewayList$ = this.gatewayList$.filter(
        (gateway) => gateway.is_active
      );
    } else if (this.quickFilter === 'inActive') {
      this.filteredGatewayList$ = this.gatewayList$.filter(
        (gateway) => !gateway.is_active
      );
    }
  }

  editGateway(gateway: any): void {
    this.isEditMode = true;
    this.editData = gateway;
    if (gateway.serial_no) {
      const serialNoExists = this.serialNoOptions.some(
        (serialNo: any) => serialNo.serial_no === gateway.serial_no
      );
      if (!serialNoExists && gateway.serial_no) {
        this.serialNoOptions = [
          ...this.serialNoOptions,
          { serial_no: gateway.serial_no }
        ];
      }
    }
    const formValues = {
      name: gateway.gateway_name,
      location_id: gateway.location,
      serialNo: gateway.serial_no,
      alarm: gateway.severity
    };
    this.form.patchValue(formValues);
    this.form.get('enableAlarm')?.enable();
    this.form.get('alarm')?.disable();
    this.form.get('alarm')?.setValue(null);
    if (gateway.alarm === true || gateway.alarm === "true") {
      this.form.get('enableAlarm')?.setValue(true);
      this.form.get('alarm')?.enable();
      this.form.get('alarm')?.setValue(gateway.severity || 'High');
    } else {
      this.form.get('enableAlarm')?.setValue(false);
    }
    this.isGatewayModalVisible = true;
  }

  deleteGateway(gateway_id: any): void {
    const obj = {
      gatewayId: gateway_id
    }
    this.deviceService.deleteGateway(obj).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        this.commonService.message.success('Gateway deleted successfully');
        this.getGatewaysbyCustomerId();
      },
      error: (error: any) => {
        this.commonService.message.error('Failed to delete gateway');
        console.error('Error deleting gateway:', error);
      }
    })
  }

  showModal(type: string): void {
    if (type === 'excel') {
      this.isExcelModalVisible = true;
    } else if (type === 'gateway') {
      this.isEditMode = false;
      this.editData = null;
      this.form.reset();
      this.isGatewayModalVisible = true;
    }
  }

  handleCancel(type: string): void {
    if (type === 'excel') {
      this.isExcelModalVisible = false;
    } else if (type === 'gateway') {
      this.isGatewayModalVisible = false;
      this.form.reset();
      this.isEditMode = false;
      this.editData = null;
      this.getSerialNo()
    }
  }
}
